/*
 * CELEBRATEVITAMINS CUSTOM — subscription widget <cv-subscription>
 * Pairs with snippets/celebrate-subscription.liquid.
 *
 * - Keeps the hidden input[name="selling_plan"] (inside the product form) in sync with the UI.
 *   Empty value = one-time purchase.
 * - Follows variant changes via Stiletto's 'product:variant-change' event
 *   (needs window.flu.states.useCustomEvents = true), with a URL/select fallback.
 * - Emits 'cv-subscription:change' (bubbles) and pushes a dataLayer event on user changes.
 * - window.CelebrateSubscription.sellingPlanFor(variantId?) → selling plan id (Number) or null,
 *   used by the custom mobile sticky ATC bar.
 */
(() => {
  if (customElements.get('cv-subscription')) return;

  const key = (freq, commit) => `${freq}_${commit}`;
  const FALLBACK_ORDER = ['m3_flex', 'm1_flex', 'm3_commit', 'm1_commit'];

  class CvSubscription extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const dataEl = this.querySelector('[data-cv-sub-data]');
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (err) {
        console.warn('[cv-subscription] invalid plan data', err);
        return;
      }

      this.input = this.querySelector('[data-cv-sub-input]');
      // The widget is its own section block, outside the product form: move the hidden
      // selling_plan input into this section's product form so the theme submits it.
      this.form = this.closest('form') ||
        (this.closest('.shopify-section') || document).querySelector('form[data-product-form]');
      if (this.form && this.input && !this.form.contains(this.input)) {
        this.form.querySelectorAll('input[name="selling_plan"][data-cv-sub-input]').forEach((old) => old.remove());
        this.form.appendChild(this.input);
      }
      this.requiresPlan = this.dataset.requiresPlan === 'true';
      this.pctTemplate = this.dataset.pctTemplate || '-__V__%';
      this.teaserTemplate = this.dataset.teaserTemplate || '';
      this.variantId = String(this.dataset.variantId);
      this.state = {
        mode: this.dataset.defaultMode === 'subscribe' ? 'subscribe' : 'onetime',
        freq: this.dataset.defaultFreq === 'm1' ? 'm1' : 'm3',
        commit: 'flex', // never pre-select a commitment
      };

      this._onChange = (e) => this.onInput(e);
      this.addEventListener('change', this._onChange);

      this._onVariant = (e) => {
        const v = e.detail && e.detail.variant;
        if (v && this.data.variants[String(v.id)]) this.setVariant(v.id);
      };
      document.addEventListener('product:variant-change', this._onVariant);

      // Fallback if custom events are off: re-read the variant after option interaction.
      this.scope = this.closest('.shopify-section') || document;
      this._onScope = (e) => {
        if (this.contains(e.target)) return;
        clearTimeout(this._t);
        this._t = setTimeout(() => this.syncVariant(), 60);
      };
      this.scope.addEventListener('change', this._onScope);
      this.scope.addEventListener('click', this._onScope);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this._onVariant);
      if (this.scope) {
        this.scope.removeEventListener('change', this._onScope);
        this.scope.removeEventListener('click', this._onScope);
      }
    }

    syncVariant() {
      const candidates = [];
      const idField = this.form && this.form.querySelector('[name="id"]');
      if (idField && idField.value) candidates.push(String(idField.value));
      try {
        const p = new URL(window.location.href).searchParams.get('variant');
        if (p) candidates.push(p);
      } catch (_) { /* noop */ }
      const next = candidates.find((id) => id !== this.variantId && this.data.variants[id]);
      if (next) this.setVariant(next);
    }

    setVariant(id) {
      id = String(id);
      if (id === this.variantId) return;
      this.variantId = id;
      this.render();
    }

    onInput(e) {
      const t = e.target;
      // Start from what is actually shown (after a variant switch the resolved plan can differ
      // from the stored state), so a click never snaps back to a fallback combination.
      if (this.current) {
        this.state.freq = this.current.freq;
        this.state.commit = this.current.commit;
      }
      if (t.matches('[data-cv-mode]')) {
        this.state.mode = t.value;
      } else if (t.matches('[data-cv-freq]')) {
        this.state.freq = t.value;
        this.state.mode = 'subscribe';
      } else if (t.matches('[data-cv-commit]')) {
        this.state.commit = t.value;
        this.state.mode = 'subscribe';
      } else {
        return;
      }
      e.stopPropagation();
      this.render();
      this.track();
    }

    pct(value) {
      return this.pctTemplate.replace('__V__', value);
    }

    setText(selector, text) {
      this.querySelectorAll(selector).forEach((el) => {
        if (el.textContent !== text) el.textContent = text;
      });
    }

    // What the shared choice (mode/freq/term) means for any variant of this product.
    resolveFor(variantId) {
      const vid = String(variantId || this.variantId);
      const v = this.data.variants[vid];
      if (!v) return null;
      const plans = v.plans || {};
      const hasAny = Object.keys(plans).some((k) => plans[k]);

      let mode = this.state.mode;
      if (!hasAny) mode = 'onetime';
      if (this.requiresPlan && hasAny) mode = 'subscribe';

      // Fall back if this variant lacks the chosen combination.
      let { freq, commit } = this.state;
      if (!plans[key(freq, commit)]) {
        // Keep the chosen term when the frequency has to change (e.g. 1-pack → 3-pack).
        const other = freq === 'm1' ? 'm3' : 'm1';
        const k = [key(other, commit), key(freq, 'flex'), ...FALLBACK_ORDER].find((x) => plans[x]);
        if (k) [freq, commit] = k.split('_');
      }
      const plan = plans[key(freq, commit)] || null;
      return { variantId: vid, variant: v, plans, hasAny, mode, freq, commit, plan, requiresPlan: this.requiresPlan };
    }

    // Change the choice from outside the widget (mobile sticky bar). Keeps both in sync.
    setChoice(partial) {
      if (this.current) {
        this.state.freq = this.current.freq;
        this.state.commit = this.current.commit;
      }
      ['mode', 'freq', 'commit'].forEach((k) => {
        if (partial && partial[k]) this.state[k] = partial[k];
      });
      this.render();
      this.track();
    }

    render() {
      const r = this.resolveFor(this.variantId);
      if (!r) return;
      const v = r.variant;
      const { plans, hasAny, mode, freq, commit, plan } = r;

      // Cards
      const subCard = this.querySelector('[data-cv-sub-card="subscribe"]');
      if (subCard) subCard.hidden = !hasAny;
      this.querySelectorAll('[data-cv-sub-card]').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.cvSubCard === mode);
      });
      this.querySelectorAll('[data-cv-mode]').forEach((i) => { i.checked = i.value === mode; });

      // Details open only while Subscribe & Save is chosen (inert keeps hidden controls out of tab order).
      const collapse = this.querySelector('[data-cv-collapse]');
      if (collapse) collapse.inert = mode !== 'subscribe';

      // Teaser on the closed option: best discount this variant offers.
      const maxPct = Object.keys(plans).reduce((m, k) => (plans[k] && plans[k].pct > m ? plans[k].pct : m), 0);
      this.setText('[data-cv-teaser]', maxPct && this.teaserTemplate ? this.teaserTemplate.replace('__V__', maxPct) : '');

      // Frequency pills
      this.querySelectorAll('[data-cv-freq]').forEach((i) => {
        const ok = !!(plans[key(i.value, 'flex')] || plans[key(i.value, 'commit')]);
        const label = i.closest('label');
        i.checked = i.value === freq;
        i.disabled = !ok;
        if (label) {
          label.hidden = !ok;
          label.classList.toggle('is-checked', i.checked);
        }
      });

      // Commitment rows
      this.querySelectorAll('[data-cv-commit]').forEach((i) => {
        const ok = !!plans[key(freq, i.value)];
        const label = i.closest('label');
        i.checked = i.value === commit;
        i.disabled = !ok;
        if (label) {
          label.hidden = !ok;
          label.classList.toggle('is-checked', i.checked);
        }
      });

      // Percentages
      this.querySelectorAll('[data-cv-pct-freq]').forEach((el) => {
        const f = el.dataset.cvPctFreq;
        const p = plans[key(f, commit)] || plans[key(f, 'flex')];
        el.textContent = p ? this.pct(p.pct) : '';
      });
      this.querySelectorAll('[data-cv-pct-commit]').forEach((el) => {
        const p = plans[key(freq, el.dataset.cvPctCommit)];
        el.textContent = p ? this.pct(p.pct) : '';
      });

      // Prices
      this.setText('[data-cv-onetime-price]', v.price);
      this.setText('[data-cv-compare]', v.price);
      if (plan) {
        this.setText('[data-cv-sub-price]', plan.price);
        this.setText('[data-cv-badge]', this.pct(plan.pct));
      }
      const unitWrap = this.querySelector('[data-cv-unit]');
      if (unitWrap) {
        unitWrap.hidden = !(plan && plan.unit);
        if (plan && plan.unit) this.setText('[data-cv-unit-value]', plan.unit);
      }

      // Hidden input → what gets added to cart
      const value = mode === 'subscribe' && plan ? String(plan.id) : '';
      const changed = this.input.value !== value;
      this.input.value = value;
      this.current = { mode, freq, commit, plan, variantId: this.variantId };

      if (changed) {
        this.dispatchEvent(new CustomEvent('cv-subscription:change', {
          bubbles: true,
          detail: {
            sellingPlanId: value ? Number(value) : null,
            mode, freq, commit,
            discountPct: plan && mode === 'subscribe' ? plan.pct : 0,
            variantId: Number(this.variantId),
          },
        }));
      }
      // Always announce the state (variant switches can change it without changing the input).
      document.dispatchEvent(new CustomEvent('cv-subscription:state', { detail: { widget: this } }));
    }

    track() {
      const c = this.current;
      if (!c) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'subscription_option_select',
        purchase_type: c.mode,
        frequency: c.mode === 'subscribe' ? c.freq : null,
        commitment: c.mode === 'subscribe' ? c.commit : null,
        selling_plan_id: c.mode === 'subscribe' && c.plan ? c.plan.id : null,
        discount_pct: c.mode === 'subscribe' && c.plan ? c.plan.pct : 0,
        variant_id: Number(c.variantId),
      });
    }
  }

  const widget = (scope) => {
    const el = (scope || document).querySelector('cv-subscription');
    return el && el.data && el.state && el.input ? el : null;
  };

  window.CelebrateSubscription = {
    widget,
    // Resolved choice for `variantId` (default: the widget's variant), same rules as the widget.
    resolve(variantId, scope) {
      const el = widget(scope);
      return el ? el.resolveFor(variantId) : null;
    },
    set(partial, scope) {
      const el = widget(scope);
      if (el) el.setChoice(partial);
    },
    labels(scope) {
      const el = widget(scope);
      return el && el.data.labels ? el.data.labels : null;
    },
    // Selling plan id for `variantId`, or null = one-time. Same rules as what the widget shows,
    // so the mobile sticky bar adds exactly what it displays.
    sellingPlanFor(variantId, scope) {
      const r = this.resolve(variantId, scope);
      return r && r.mode === 'subscribe' && r.plan ? Number(r.plan.id) : null;
    },
  };

  // Define last: upgrading existing elements renders immediately and the API must exist by then.
  customElements.define('cv-subscription', CvSubscription);
})();
